import { ClickableTextPanel } from "@/Common/Components/ClickableText/ClickableTextPanel";
import { Locale } from "@/Common/Models/Localization/Locale";
import { WeightUnit } from "@/Common/Utilities/Units/Units";
import { ObstetricalFileControlRenderer } from "@/Patient/Components/MedicalNote/ObstetricalFile/ControlRenderer/ObstetricalFileControlRenderer";
import { ObstetricalFileMedicalSummary } from "@/Patient/Models/ObstetricalFile/ObstetricalFileMedicalSummary";
import { ObstetricalVisit } from "@/Patient/Models/ObstetricalFile/ObstetricalVisit";
import { PrenatalVisitInitials } from "@/Patient/Models/ObstetricalFile/PrenatalVisitInitials";
import { BaseObstetricalFileSubSection, BaseObstetricalFileSubSectionProps } from "./BaseObstetricalFileSubSection";
import { IObstetricalFileSubsequentVisitsTableRowProps } from "./ObstetricalFileSubsequentVisitsTableRow";
import { WeightUnitComponent } from "./WeightUnitComponent";

interface IObstetricalFileSubsequentVisitsTableProps<TForm, TVisit extends ObstetricalVisit> extends BaseObstetricalFileSubSectionProps<TForm> {
    visits: TVisit[];
    obstetricalFileMedicalSummary: ObstetricalFileMedicalSummary;
    onChange: (
        visits: TVisit[],
        currentVisitChange: TVisit,
        newInitialsMapping: string,
        shouldHandleFormUpdate: boolean
    ) => void;
    onWeightUnitChange: (unit: WeightUnit) => void;
    onVitalSignUpdated: () => void;
    onAddSubsequentVisitRow: () => void;
    vitalSignControlRenderer: ObstetricalFileControlRenderer;
    existingInitials: PrenatalVisitInitials[];
    heightInMeters: number;
}

class ObstetricalFileSubsequentVisitsTableState {
    public subsequentVisitsClickableTextContext: string;
}

abstract class ObstetricalFileSubsequentVisitsTable<TForm, TVisit extends ObstetricalVisit> extends BaseObstetricalFileSubSection<TForm, IObstetricalFileSubsequentVisitsTableProps<TForm, TVisit>, ObstetricalFileSubsequentVisitsTableState> {
    protected abstract readonly clickableContext: string;

    protected get tableContainerStyle(): React.CSSProperties { return {}; }

    constructor(props: IObstetricalFileSubsequentVisitsTableProps<TForm, TVisit>) {
        super(props);
        this.state = {
            subsequentVisitsClickableTextContext: null
        };
    }

    public componentDidMount(): void {
        window.addEventListener("click", this.handleOnWindowClick);
        this.setAcceptableLocales([Locale.en]);
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        window.removeEventListener("click", this.handleOnWindowClick);
    }

    private handleOnWindowClick = (): void => {
        this.mutateState(s => s.subsequentVisitsClickableTextContext = null);
    };

    public render(): JSX.Element {
        const commentsIdPrefix = "Comments_";
        const visits = this.props.visits;
        const clickableTextId = `${commentsIdPrefix}${visits.length - 1}`;

        return (
            <div>
                {this.renderTitle()}
                {this.renderAboveTableExtra()}
                <div className="row row-form" style={this.tableContainerStyle}>
                    <div className="col-md-12">
                        <table className="margin-none subsequent-visit sticky-header">
                            {this.renderTableHeader()}
                            <tbody>
                                {visits?.map((visit, idx) => this.renderTableRow(visit, idx))}
                            </tbody>
                        </table>
                        {this.renderAddNewVisitLink()}
                        {!this.props.readonly && this.state.subsequentVisitsClickableTextContext && this.props.clickableTextId == clickableTextId && (
                            <ClickableTextPanel
                                cssClass="text-helper-container"
                                context={this.clickableContext}
                                inputElementId={clickableTextId}
                                onCloseEvent={() => this.mutateState(s => s.subsequentVisitsClickableTextContext = null)}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    }

    protected abstract renderTitle(): JSX.Element;
    protected abstract renderTableHeader(): JSX.Element;
    protected abstract renderTableRow(visit: TVisit, index: number): JSX.Element;
    protected renderAboveTableExtra(): JSX.Element { return null; }

    protected renderAddNewVisitLink(): JSX.Element {
        if (!this.props.onAddSubsequentVisitRow)
            return null;

        return (
            <div>
                <a onClick={() => this.props.onAddSubsequentVisitRow()}>
                    {`[+] ${cacheStore.GetString("AddNewVisitLabel", null, Locale.en)}`}
                </a>
            </div>
        );
    }

    protected renderWeightUnit(selectedUnit: WeightUnit): JSX.Element {
        return (
            <WeightUnitComponent
                units={[WeightUnit.Kilogram, WeightUnit.Pound]}
                selectedUnit={selectedUnit}
                onUnitChange={this.props.onWeightUnitChange}
                readonly={this.props.readonly}
                acceptableLocales={[Locale.en]}
            />
        );
    }

    protected getTableRowProps(visit: TVisit, index: number): IObstetricalFileSubsequentVisitsTableRowProps<TForm, TVisit> & React.Attributes {
        const commentsIdPrefix = `Comments_${index}`;
        const visits = this.props.visits;

        return {
            ...this.props,
            visit: visit,
            onChange: (data, shouldHandleFormUpdate, isInitialsChange) => this.onRowChange(index, data, shouldHandleFormUpdate, isInitialsChange),
            onSelectFromTextBtnClick: () => this.mutateState(
                s => s.subsequentVisitsClickableTextContext = this.clickableContext,
                () => this.props.onClickableTextChange?.(commentsIdPrefix, this.clickableContext)
            ),
            index: index,
            commentsId: commentsIdPrefix,
            isLastRow: index == visits.length - 1,
            key: visit.getId()
        };
    }

    private onRowChange(rowIndex: number, visit: TVisit, shouldHandleFormUpdate: boolean, isInitialsChange: boolean): void {
        let newInitialsMapping: string;

        if (isInitialsChange)
            visit.Initials.tryAddUserContactFacetId(sessionStore.getCurrentEmployee().ContactFacetId);
        else {
            const employee = sessionStore.getCurrentEmployee();
            const initials = visit.Initials.addInitialsFromUser(employee, this.props.existingInitials, ", ");
            if (initials)
                newInitialsMapping = employee.nameWithPrefix() + "/" + initials;
        }

        const newData = [...this.props.visits];
        newData[rowIndex] = visit;

        this.props.onChange(newData, visit, newInitialsMapping, shouldHandleFormUpdate);
    }
}

export { IObstetricalFileSubsequentVisitsTableProps, ObstetricalFileSubsequentVisitsTable };
